import type { PortEnv, PhpRef } from '../../port-contract';
import { PhpArray, phpCopy, empty } from '../../php-compat';
import { at, entries, isArray, has, renderJson, type Row } from './support';
export interface InventoryRenderDeps {
    'CardListItemLayer::parseValue'(entry: unknown): Row | null;
    'CardListItemLayer::getCostGp'(base: Row, enchantment: Row | null): unknown;
    list_display_inventory_thumbnail_url(base: Row): string | null;
    item_field(item: Row, name: string): unknown;
    ft_creature_worn_equipped_render_arr(item: Row): Row;
    build_static_item_creature_equipped_inventory(item: PhpRef<Row>): void;
    ft_build_referenced_names_map(item: Row): Row;
}
/** PHP: _ft_ld_ts_enrich_inventory_entry (includes/functions/janus/core/ld_ts_mount.php:201) */
export function FtLdTsEnrichInventoryEntry(entry: PhpRef<Row>, env: PortEnv<InventoryRenderDeps>, copy: typeof phpCopy = phpCopy): null {
    try {
        const d = env.deps, parsed = d['CardListItemLayer::parseValue'](entry.value);
        if (!parsed || empty(at(parsed, 'item_arr'))) return null;
        const base = at(parsed, 'item_arr') as Row, ench = at(parsed, 'enchantment_template_arr');
        const cost = d['CardListItemLayer::getCostGp'](base, isArray(ench) ? ench : null);
        const pic = d.list_display_inventory_thumbnail_url(base);
        const name = isArray(ench) ? d.item_field(ench, 'item_name') ?? null : null;
        const result = new PhpArray(entries(copy(entry.value)));
        result.set('_ft_display', { cost_gp: cost, pic_url: pic, enchantment_template_name: name }); entry.value = result;
    } catch { /* PHP best-effort display enrichment catches Throwable. */ }
    return null;
}
/** PHP: _ft_ld_ts_enrich_inventory_slot (includes/functions/janus/core/ld_ts_mount.php:186) */
export function FtLdTsEnrichInventorySlot(slot: PhpRef<Row>, env: PortEnv<InventoryRenderDeps>, copy: typeof phpCopy = phpCopy): null {
    if (!has(slot.value, '0')) return FtLdTsEnrichInventoryEntry(slot, env, copy);
    const result = new PhpArray(entries(copy(slot.value)));
    for (const [key, entry] of entries(result)) if (isArray(entry)) { const ref = { value: entry }; FtLdTsEnrichInventoryEntry(ref, env, copy); result.set(key, ref.value); }
    slot.value = result;
    return null;
}
/** PHP: ft_ld_ts_enrich_inventory (includes/functions/janus/core/ld_ts_mount.php:175) */
export function ftLdTsEnrichInventory(item: PhpRef<Row>, env: PortEnv<InventoryRenderDeps>, copyValues: typeof phpCopy = phpCopy): null {
    const copy = new PhpArray(entries(copyValues(item.value)));
    for (const layer of ['inventory_equipped', 'inventory_unequipped', 'spell_material_item_id']) {
        const bucketSlot = at(copy, 'item_layer_types', layer), topSlot = at(copy, layer);
        if (isArray(bucketSlot)) {
            const ref = { value: bucketSlot }; FtLdTsEnrichInventorySlot(ref, env, copyValues);
            const bucket = new PhpArray(entries(at(copy, 'item_layer_types') as Row)); bucket.set(layer, ref.value); copy.set('item_layer_types', bucket);
        } else if (isArray(topSlot)) { const ref = { value: topSlot }; FtLdTsEnrichInventorySlot(ref, env, copyValues); copy.set(layer, ref.value); }
    }
    item.value = copy;
    return null;
}
/** PHP: ft_ld_ts_inventory_mount (includes/functions/janus/core/ld_ts_mount.php:393) */
export function ftLdTsInventoryMount(item: Row, env: PortEnv<InventoryRenderDeps>): string {
    const d = env.deps, ref = { value: phpCopy(item) };
    if ((d.item_field(ref.value, 'item_type') ?? '') === 'creature' && empty(at(ref.value, 'encounter_creatures', 'encounter_creature_id'))) {
        ref.value = new PhpArray(entries(d.ft_creature_worn_equipped_render_arr(ref.value)));
        (ref.value as PhpArray).delete('inventory'); d.build_static_item_creature_equipped_inventory(ref);
    }
    ftLdTsEnrichInventory(ref, env); const names = d.ft_build_referenced_names_map(ref.value);
    const blob = renderJson(ref.value), nameJson = renderJson(names);
    return '<div class="ft-ld-ts-list" data-ld-ts-mode="inventory"><script type="application/json" class="ft-ld-ts-blob">' + (blob !== false ? blob : 'null').replace(/<\//g, '<\\/') + '</script><script type="application/json" class="ft-ld-ts-names">' + (nameJson !== false ? nameJson : '{}').replace(/<\//g, '<\\/') + '</script></div>';
}
